import { type User as CoreUser } from '@grammarly/http-clients-auth-v5'

import { type ClientCapabilities, getClientCapabilities } from '@lib/utils/client-capabilities'
import { isEmpty } from '@lib/utils/object'

import type {
  TypePageEntry,
  TypeSeoMetadataEntry,
  TypeSocialMetadataEntry,
} from '@typings/contentful'

import { IS_BROWSER } from '@constants/globals'

import type { AgentProfileProps } from '@custom/pages/agent-profile/types'

declare global {
  interface Window {
    ShData: InjectedServerData
  }
}

export interface InjectedServerData {
  readonly requestId: string
  readonly pageSlug?: string
  readonly user?: CoreUser
  readonly clientCapabilities?: ClientCapabilities
  readonly heroImageSeed?: number | null
  readonly extras?: {
    readonly plansData?: PlansData
    readonly agentProfile?: AgentProfileProps
  }
}

export interface PlansData {
  readonly bundleProPlans: readonly BackendPlan[]
  readonly bundleBusinessPlans: readonly BackendPlan[]
}

export interface RenderRequestServerData extends InjectedServerData {
  readonly pageEntry?: TypePageEntry
  readonly metadata?: {
    readonly social?: TypeSocialMetadataEntry
    readonly seo?: TypeSeoMetadataEntry
    readonly enabledFlags?: string[]
  }
  readonly passThroughCookies: Record<string, string | undefined>
}

export interface BackendPlan {
  readonly id: number
  readonly renewalPlanId: number
  readonly regularPlanId: number
  readonly title: 'Annual' | 'Monthly' | 'Quarterly'
  readonly description: string
  readonly regularPrice: number
  readonly regularPriceMoney: Money
  readonly price: number
  readonly priceMoney: Money
  readonly renewalPrice: number
  readonly renewalPriceMoney: Money
  readonly periodMonths: number
  readonly hasTrial: boolean
  readonly trialDays: number
  readonly baseInstitutionCampaign: boolean
  readonly priceTiers?: readonly PriceTier[]
}

export interface PriceTier {
  readonly price: number
  readonly priceMoney: Money
  readonly fromSeats: number
  readonly toSeats: number
}
export interface Money {
  readonly currency: string
  readonly value: number
}

export type UserAgentString = string

export interface ServerService {
  get: () => InjectedServerData
  pick: (key: keyof InjectedServerData) => InjectedServerData[keyof InjectedServerData]
}

export class ServerServiceImpl implements ServerService {
  private readonly cachedData: InjectedServerData

  static getDefaultServerData(userAgent?: UserAgentString): InjectedServerData {
    return {
      requestId: 'unknown',
      pageSlug: 'unknown',
      clientCapabilities: getClientCapabilities(userAgent),
    }
  }

  constructor(requestData?: RenderRequestServerData, userAgent?: UserAgentString) {
    this.cachedData =
      requestData && !isEmpty(requestData)
        ? {
            requestId: requestData.requestId,
            pageSlug: requestData.pageSlug,
            user: requestData.user,
            clientCapabilities: requestData.clientCapabilities,
            heroImageSeed: requestData.heroImageSeed,
            extras: requestData.extras,
          }
        : (IS_BROWSER && window?.ShData) ||
          ServerServiceImpl.getDefaultServerData(
            IS_BROWSER ? window?.navigator?.userAgent : userAgent,
          )
  }

  get(): InjectedServerData {
    return this.cachedData
  }

  pick(key: keyof InjectedServerData): InjectedServerData[keyof InjectedServerData] {
    return this.get()[key]
  }
}
